import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Public Password Reset Page
 *
 * This page is accessible without authentication and provides
 * a self-service password reset widget for students who are
 * locked out of their accounts.
 *
 * For Azure AD federation, this redirects to the Azure AD
 * password reset flow.
 */
export default function PublicPasswordReset() {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate email is a UEL email
    if (!email.endsWith('@uel.ac.uk') && !email.endsWith('@mail.uel.ac.uk')) {
      setError(t('invalidUelEmail'));
      return;
    }

    // For Azure AD federation, redirect to Azure AD password reset
    // In production, this would be configured with the correct tenant
    const azureResetUrl = 'https://passwordreset.microsoftonline.com/';

    // Redirect to Azure AD password reset
    window.location.href = azureResetUrl;

    // If not using Azure AD, would call the API here
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-uel-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-2xl">P</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t('resetPasswordTitle')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              {t('resetPasswordSubtitle')}
            </p>
          </div>

          {submitted ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-green-600 dark:text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                {t('checkYourEmail')}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t('resetEmailSent', { email })}
              </p>
              <a
                href="/"
                className="text-uel-purple-600 dark:text-uel-purple-400 hover:underline"
              >
                {t('returnToPaila')}
              </a>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  {t('uelEmailLabel')}
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="u1234567@uel.ac.uk"
                  required
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg
                    bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                    focus:ring-2 focus:ring-uel-purple-500 focus:border-transparent
                    transition-colors"
                />
              </div>

              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg" role="alert">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              <button
                type="submit"
                className="w-full px-4 py-3 bg-uel-purple-600 text-white font-medium rounded-lg
                  hover:bg-uel-purple-700 focus:ring-2 focus:ring-uel-purple-500 focus:ring-offset-2
                  transition-colors"
              >
                {t('resetPassword')}
              </button>
            </form>
          )}

          {/* Help Section */}
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
              {t('needMoreHelp')}
            </h3>
            <div className="space-y-2 text-sm">
              <a
                href="https://www.uel.ac.uk/it"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-gray-600 dark:text-gray-400 hover:text-uel-purple-600 dark:hover:text-uel-purple-400"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                {t('itHelpDesk')}
              </a>
              <a
                href="mailto:ithelpdesk@uel.ac.uk"
                className="flex items-center text-gray-600 dark:text-gray-400 hover:text-uel-purple-600 dark:hover:text-uel-purple-400"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                ithelpdesk@uel.ac.uk
              </a>
            </div>
          </div>
        </div>

        {/* Back to login */}
        <div className="mt-6 text-center">
          <a
            href="/"
            className="text-sm text-gray-600 dark:text-gray-400 hover:text-uel-purple-600 dark:hover:text-uel-purple-400"
          >
            {t('backToPaila')}
          </a>
        </div>
      </div>
    </div>
  );
}
